// EmailWelcomeSequence.js
// Vonzás 02 · Email Varázsló · 1. kártya — Üdvözlő szekvencia eredmény.
//
// FONTOS ARCHITEKTURÁLIS DÖNTÉS:
// - Ez a komponens NEM rendel saját chrome-ot (header, tabok) — azt a
//   szülő `EmailAgent` adja a Poszt Varázsló `PostHeader`-jét reuse-olva.
// - Csak a 2-paneles tartalmat (bal finomító panel · jobb krém dokumentum)
//   renderelni — pontosan a Poszt Varázsló result view mintájára.
//
// VIZUÁLIS RENDSZER:
//   - BAL panel (finomító): identikus a Poszt Varázsló refine panel-jével,
//     a SAME LILA TOKENEK (#7c6cf2 / rgba(124,108,242,*)) — selection box,
//     quick chips, KIJELÖLT RÉSZ box, chat input, send gomb.
//   - JOBB panel (krém dokumentum): EGYETLEN szín-akcent a `--accent-blue`
//     (#26A9C9) változó. Tintek: pill `rgba(38, 169, 201,0.14)` border `0.35`,
//     block `0.10` border `0.30`.
//
// Per-email block szerkezet (TASK 3 figyelembe):
//   (a) elválasztó MARGÓ a kontrollsortól (sketchSection wrapper top:48px)
//   (b) HEADER bold ~21px near-black "Email #N" + jobbra TIMING INFO TAG
//       — FLAT, nem-kattintható, light kék tint + kék text, nincs button
//       szegély vagy hover effekt. (TASK 3 spec).
//   (c) PURPOSE BLOCK — "A LEVÉL CÉLJA" eyebrow + 1 mondatos cél.
//   (d) "TÁRGYSOR-OPCIÓK" sub-eyebrow + 3 pill (első=ajánlott, solid border).
//   (e) "SZÖVEGTÖRZS" sub-eyebrow + body (inline editable chip-ek a [...]
//       helykitöltőkből).
//   (f) Vékony divider az e-mailek között.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  CircleNotch, Copy as CopyIcon,
  DownloadSimple, CheckCircle, Paperclip, PaperPlaneTilt,
  X as XIcon, Clock,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { SuggestionCard, AppliedBlock } from "../components/AgentOutputScreen";

const ACCENT_BLUE = "#26A9C9";
const ACCENT_BLUE_DARK = "#135b6c";
const ACCENT_BLUE_TINT_PILL = "rgba(38, 169, 201, 0.14)";
const ACCENT_BLUE_TINT_BLOCK = "rgba(38, 169, 201, 0.10)";
const ACCENT_BLUE_BORDER_BLOCK = "rgba(38, 169, 201, 0.30)";
// Faint-blue subject pill colors (slightly lighter than the framed block).
const SUBJECT_PILL_BG = "rgba(38, 169, 201, 0.08)";
const SUBJECT_PILL_BORDER = "rgba(38, 169, 201, 0.35)";

const QUICK_REFINE_ACTIONS = [
  { id: "edge",     label: "Élesebb" },
  { id: "soft",     label: "Lágyabb" },
  { id: "concrete", label: "Konkrétabb" },
  { id: "story",    label: "Több sztori" },
  { id: "humor",    label: "Humorosabb" },
  { id: "surprise", label: "Lepj meg!" },
];

// FIXED, descriptive email titles per POSITION. A fejléc a sorrendből
// veszi a címet — NEM a generált `nev` mezőből —, így a UI mindig a
// stratégiai elnevezést mutatja, függetlenül az LLM kimenettől.
const WELCOME_FIXED_TITLES = [
  "Mi minden vár rád?",
  "A régi módszer csapdája",
  "Meghívás az új útra",
];

// "Nap N" → emberi időcímke (clock + rövid érték).
// TASK 3: az új design — solid filled tag, ikon + rövid szöveg, nincs
// "Kiküldés:" prefix; a tag ÖNMAGÁBAN érthető (clock-ikon ad kontextust).
function timingValueFromNap(nap) {
  const n = Number(nap);
  if (n <= 0 || Number.isNaN(n)) return "azonnal";
  if (n === 1) return "1 nap múlva";
  return `${n} nap múlva`;
}

export default function EmailWelcomeSequence({
  initialContent = null,
  initialRawText = "",
}) {
  // Result-only komponens — a szülő EmailAgent intézi a generálást.
  const [content, setContent] = useState(initialContent);
  const [rawText, setRawText] = useState(initialRawText);
  const [chosenSubjects, setChosenSubjects] = useState(() => {
    const defaults = {};
    (initialContent?.emails || []).forEach((e) => { defaults[e.email_szam] = 0; });
    return defaults;
  });

  // Sync ha a szülő új tartalmat ad (újragenerálás után).
  useEffect(() => {
    setContent(initialContent);
    setRawText(initialRawText);
    const defaults = {};
    (initialContent?.emails || []).forEach((e) => { defaults[e.email_szam] = 0; });
    setChosenSubjects(defaults);
  }, [initialContent, initialRawText]);

  // Cream action pillek
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingVault, setSavingVault] = useState(false);

  // Finomító state-machine
  const [selection, setSelection] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [refinePhase, setRefinePhase] = useState("idle");
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionError, setSuggestionError] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const undoTimerRef = useRef(null);
  const previewRef = useRef(null);

  const composedAllText = useMemo(() => {
    if (!content?.emails?.length) return rawText || "";
    return content.emails.map((em) => {
      const idx = chosenSubjects[em.email_szam] ?? 0;
      const subjects = em.targysorok || [];
      const subj = subjects[idx] || subjects[0] || "";
      const lines = [
        em.nev
          ? `EMAIL ${em.email_szam} - ${em.nev} - Nap ${em.nap}`
          : `EMAIL ${em.email_szam} - Nap ${em.nap}`,
      ];
      if (em.cel) lines.push(`Cél: ${em.cel}`);
      if (subjects.length) {
        lines.push("Tárgysor-opciók (az első az ajánlott):");
        subjects.forEach((t, i) => lines.push(`${i + 1}. ${t}`));
      } else {
        lines.push(`Tárgy: ${subj}`);
      }
      lines.push("");
      lines.push(em.body || "");
      return lines.join("\n");
    }).join("\n\n");
  }, [content, chosenSubjects, rawText]);

  // Selection capture a cream cardon
  useEffect(() => {
    const handleMouseUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const text = sel.toString().trim();
      if (text.length < 3) return;
      const root = previewRef.current;
      if (!root) return;
      let node = sel.anchorNode;
      while (node && node !== root) node = node.parentNode;
      if (!node) return;
      setSelection(text.slice(0, 600));
    };
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, []);

  const clearSelection = () => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  // ─── Refine pipeline ──────────────────────────────────────────────
  // POSZT-MINTÁJÚ MOD-LOGIKA:
  //   mod="snippet" → a Writer CSAK a kijelölt rész új változatát adja
  //     vissza (a `snippet` mezőben). A SuggestionCard ezt mutatja preview-
  //     ként (NEM az egész sorozatot). Az `applySuggestion` lokálisan
  //     cseréli a quote-ot a snippet-re a rawText-ben — a sorozat többi
  //     része byte-szinten változatlan.
  //   mod="teljes" → nincs kijelölés, a Writer a teljes sorozatot újra
  //     adja vissza (eddig is így volt). Az `applySuggestion` az egészet
  //     felülírja.
  const requestRefine = async (instructionOverride, quickButtonId = null) => {
    const utasitas = (instructionOverride || chatInput).trim();
    if (!utasitas && !quickButtonId) return;
    if (refinePhase === "loading" || refinePhase === "suggestion") return;
    const quoteToSend = selection;
    setSuggestionError(false);
    setRefinePhase("loading");
    if (!instructionOverride) setChatInput("");
    try {
      const res = await api.post("/attraction/email/welcome/refine", {
        jelenlegi_sorozat: composedAllText,
        utasitas: utasitas || "",
        quote: quoteToSend || null,
        quick_button: quickButtonId || null,
      });
      const data = res.data || {};
      const mode = data?.metadata?.mode === "snippet" ? "snippet" : "teljes";
      setSuggestion({
        mod: mode,
        snippet: data.snippet || "",     // csak snippet módban
        teljes: data.teljes_szoveg || "", // csak teljes módban használt apply-kor
        content: data.welcome_content || null,
        instr: utasitas || (quickButtonId || ""),
        quote: quoteToSend || null,
      });
      setRefinePhase("suggestion");
    } catch (e) {
      setSuggestion({
        mod: "error",
        instr: utasitas || (quickButtonId || ""),
        quote: quoteToSend || null,
      });
      setSuggestionError(true);
      setRefinePhase("suggestion");
    }
  };

  const retryVariant = async () => {
    if (!suggestion || regenerating) return;
    const previousSnippet =
      suggestion.mod === "snippet" ? (suggestion.snippet || "") : (suggestion.teljes || "");
    setRegenerating(true);
    setSuggestionError(false);
    try {
      const res = await api.post("/attraction/email/welcome/refine", {
        jelenlegi_sorozat: composedAllText,
        utasitas: (suggestion.instr || "") +
          "\n\nGenerálj egy érdemben MÁSIK változatot, mint az előzőt." +
          (previousSnippet ? ` Az előző javaslat eleje így szólt: "${previousSnippet.slice(0, 280)}". Kerüld el az ismétlést.` : ""),
        quote: suggestion.quote || null,
      });
      const data = res.data || {};
      const mode = data?.metadata?.mode === "snippet" ? "snippet" : "teljes";
      setSuggestion((s) => ({
        ...s,
        mod: mode,
        snippet: data.snippet || "",
        teljes: data.teljes_szoveg || "",
        content: data.welcome_content || null,
      }));
    } catch {
      setSuggestionError(true);
    } finally {
      setRegenerating(false);
    }
  };

  const retryAfterError = () => {
    if (!suggestion) return;
    setSuggestionError(false);
    requestRefine(suggestion.instr);
  };

  const cancelSuggestion = () => {
    setSuggestion(null);
    setSuggestionError(false);
    setRefinePhase("idle");
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    const prevText = composedAllText;
    let nextText = prevText;

    if (suggestion.mod === "snippet" && suggestion.snippet && suggestion.quote) {
      // LOKÁLIS string-csere: kizárólag a kijelölt rész cserélődik,
      // minden más byte-szinten változatlan.
      const idx = prevText.indexOf(suggestion.quote);
      if (idx >= 0) {
        nextText =
          prevText.slice(0, idx) +
          suggestion.snippet +
          prevText.slice(idx + suggestion.quote.length);
      } else {
        // Quote nem található — ne módosítsunk, hibázzunk csendben.
        setRefinePhase("idle");
        setSuggestion(null);
        return;
      }
    } else if (suggestion.mod === "teljes" && suggestion.teljes) {
      nextText = suggestion.teljes;
    } else {
      // Hiba vagy üres javaslat — nem alkalmazunk semmit.
      setRefinePhase("idle");
      setSuggestion(null);
      return;
    }

    setUndoSnapshot({ content, rawText, chosenSubjects });
    setRawText(nextText);

    // Snippet módban a structured content-et a régi szerkezetből építjük
    // (csak a body-k cserélődnek, a tárgysorok és cél változatlanok).
    // Teljes módban a backend reparseolt strukturált content-et használjuk.
    if (suggestion.mod === "snippet") {
      // A frontend nem rendelkezik parser-rel, de a body-ban végzett
      // string-csere a meglévő `content` objektum body mezőin is
      // elvégezhető. Ezzel a strukturált renderelés helyes marad.
      if (content && Array.isArray(content.emails)) {
        const nextEmails = content.emails.map((em) => {
          const oldBody = em.body || "";
          if (oldBody.includes(suggestion.quote)) {
            return { ...em, body: oldBody.replace(suggestion.quote, suggestion.snippet) };
          }
          return em;
        });
        setContent({ ...content, emails: nextEmails });
      }
    } else {
      // Teljes mód: a server által visszaadott struktúrát használjuk.
      const nextContent = suggestion.content || null;
      const nextEmails = Array.isArray(nextContent?.emails) ? nextContent.emails : [];
      if (nextEmails.length > 0) {
        setContent(nextContent);
        const next = {};
        nextEmails.forEach((e) => { next[e.email_szam] = chosenSubjects[e.email_szam] ?? 0; });
        setChosenSubjects(next);
      } else {
        setContent(null);
      }
    }

    setSelection(null);
    setSaved(false);
    window.getSelection()?.removeAllRanges();
    setTimeout(() => { try { window.getSelection()?.removeAllRanges(); } catch { /* noop */ } }, 0);
    setSuggestion(null);
    setRefinePhase("applied");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setRefinePhase("idle");
      setUndoSnapshot(null);
    }, 8000);
  };

  const undoApplied = () => {
    if (!undoSnapshot) return;
    setContent(undoSnapshot.content);
    setRawText(undoSnapshot.rawText);
    setChosenSubjects(undoSnapshot.chosenSubjects);
    setUndoSnapshot(null);
    setRefinePhase("idle");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  };

  useEffect(() => () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, []);

  const handleSubmitChat = () => requestRefine();

  // ─── Krém-akciók — CSAK: Másolás / Letöltés / Mentés. TASK 2: ────
  // a "Beállítás" pill ELTÁVOLÍTVA innen (chrome toggle-ben él).
  const handleCopy = async () => {
    if (!composedAllText) return;
    try {
      await navigator.clipboard.writeText(composedAllText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Vágólapra másolva");
    } catch {
      toast.error("Másolás nem sikerült");
    }
  };

  // Egyetlen e-mail body-jának másolása (per-email copy ikon).
  const handleCopyOne = async (em) => {
    if (!em || !em.body) return;
    try {
      await navigator.clipboard.writeText(em.body);
      toast.success("Levél másolva");
    } catch {
      toast.error("Másolás nem sikerült");
    }
  };

  const handleDownload = () => {
    if (!composedAllText) return;
    const blob = new Blob([composedAllText], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "udvozlo-szekvencia.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const handleSave = async () => {
    if (!composedAllText || saved || savingVault) return;
    setSavingVault(true);
    try {
      await api.post("/attraction/email/welcome/save", {
        teljes_szoveg: composedAllText,
        welcome_content: content || null,
        chosen_subjects: chosenSubjects,
      });
      setSaved(true);
      toast.success("Mentve a Tudástárba");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mentés sikertelen.");
    } finally {
      setSavingVault(false);
    }
  };

  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
  const isLoading = refinePhase === "loading";
  const showSuggestion = refinePhase === "suggestion" && suggestion;
  const showApplied = refinePhase === "applied";
  const inputDisabled = isLoading || showSuggestion;

  const hasStructured = content?.emails?.length > 0;
  const placeholder = selection
    ? "Mit változtassak a kijelölt részen?"
    : "Mit szeretnél finomítani?";
  const suggestionSnippet = suggestion
    ? (suggestion.mod === "snippet" ? (suggestion.snippet || "") : (suggestion.teljes || ""))
    : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="flex flex-col h-full"
      style={{ background: "#0a0a12" }}
      data-testid="email-welcome-shell"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">
        {/* ─── LEFT — Finomító Panel (IDENTIKUS A POSZT REFINE PANELLEL,
             SAME LILA TOKENEK; TASK 5) ────────────────────────────── */}
        <div
          className="flex flex-col border-r min-h-0 h-full"
          style={{ borderColor: "rgba(255,255,255,0.04)" }}
          data-testid="email-welcome-finomito"
        >
          <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
            <div style={{ maxWidth: 480 }}>
              <div
                className="font-heading"
                style={{
                  fontSize: 28, fontWeight: 600, lineHeight: 1.3,
                  letterSpacing: "-0.015em", color: "#ffffff", marginBottom: 18,
                }}
              >
                Elkészült a sorozat.
              </div>
              <div style={{ fontSize: 17, fontWeight: 400, lineHeight: 1.7, color: "#c0c0d0" }}>
                Olvasd át a jobb oldalon. Ha minden rendben, mentsd el a Tudástárba. Ha valamin finomítanál, jelölj ki egy részt a szövegben, vagy írd lent, mit változtassak.
              </div>
            </div>

            <AnimatePresence>
              {(selection || isLoading || showSuggestion) && (
                <motion.div
                  key="workspace"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    background: "rgba(124,108,242,0.025)",
                    border: "1px solid rgba(124,108,242,0.10)",
                    borderRadius: 12, padding: 14, margin: "32px -8px 0",
                  }}
                  data-testid="email-welcome-selection-workspace"
                >
                  {(selection || (suggestion && suggestion.quote)) && (
                    <div
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        borderLeft: "2px solid rgba(124,108,242,0.40)",
                        borderRadius: 8, padding: "11px 13px", marginBottom: 12,
                      }}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0">
                          <div style={{
                            fontSize: 9, textTransform: "uppercase", letterSpacing: "0.16em",
                            color: "#8a8a9c", fontWeight: 600, marginBottom: 4,
                          }}>KIJELÖLT RÉSZ</div>
                          <div style={{
                            fontSize: 12.5, color: "#c0c0d0", lineHeight: 1.5,
                            display: "-webkit-box", WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical", overflow: "hidden",
                          }}>
                            {(() => {
                              const q = selection || suggestion?.quote || "";
                              return `„${q.length > 240 ? q.slice(0, 240) + "…" : q}"`;
                            })()}
                          </div>
                        </div>
                        {!showSuggestion && !isLoading && (
                          <button
                            type="button" onClick={clearSelection}
                            aria-label="Kijelölés törlése"
                            style={{
                              width: 22, height: 22, borderRadius: 5,
                              background: "rgba(255,255,255,0.04)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              color: "#9898ad", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}
                            data-testid="email-welcome-selection-clear"
                          ><XIcon size={11} weight="bold" /></button>
                        )}
                      </div>
                    </div>
                  )}

                  {!isLoading && !showSuggestion && selection && (
                    <div
                      className="grid grid-cols-2 sm:grid-cols-3 gap-2.5"
                      data-testid="email-welcome-quick-actions"
                    >
                      {QUICK_REFINE_ACTIONS.map((qa) => (
                        <button
                          key={qa.id} type="button"
                          onClick={() => requestRefine(null, qa.id)}
                          data-testid={`email-welcome-quick-${qa.id}`}
                          style={{
                            fontSize: 13, padding: "10px 14px", borderRadius: 999,
                            background: "rgba(124,108,242,0.10)",
                            border: "1px solid rgba(124,108,242,0.28)",
                            color: "#cfc8ff", cursor: "pointer",
                            fontFamily: "inherit", fontWeight: 500,
                            whiteSpace: "nowrap", textAlign: "center", width: "100%",
                          }}
                        >{qa.label}</button>
                      ))}
                    </div>
                  )}

                  {isLoading && (
                    <div
                      className="flex items-center gap-2.5"
                      style={{ fontSize: 13, color: "#a8a8c0", padding: "4px 2px" }}
                      data-testid="email-welcome-refine-loading"
                    >
                      <CircleNotch size={14} weight="bold" className="animate-spin" />
                      Finomítás folyamatban…
                    </div>
                  )}

                  {showSuggestion && (
                    <div data-testid="email-welcome-suggestion">
                      <SuggestionCard
                        snippet={suggestionSnippet}
                        isError={suggestionError}
                        regenerating={regenerating}
                        onApply={applySuggestion}
                        onRetryVariant={retryVariant}
                        onRetryAfterError={retryAfterError}
                        onCancel={cancelSuggestion}
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showApplied && (
                <motion.div
                  key="applied"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ margin: "24px 0 0" }}
                  data-testid="email-welcome-applied"
                >
                  <AppliedBlock onUndo={undoApplied} canUndo={!!undoSnapshot} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Bottom chat input — IDENTIKUS A POSZT REFINE PANELLEL */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (chatInput.trim() && !inputDisabled) handleSubmitChat();
            }}
            style={{ padding: "12px 24px 24px" }}
            data-testid="email-welcome-finomito-form"
          >
            <div style={{
              background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
              border: "1px solid rgba(243,238,228,0.14)",
              borderRadius: 12,
              padding: "5px 5px 5px 14px",
              display: "flex", alignItems: "center", gap: 10,
              boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
              opacity: inputDisabled ? 0.5 : 1,
              pointerEvents: inputDisabled ? "none" : "auto",
            }}>
              <button type="button" aria-label="Csatolás"
                style={{
                  background: "transparent", border: "none", padding: 0,
                  color: "#8a8a9c", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}>
                <Paperclip size={14} weight="regular" />
              </button>
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    if (chatInput.trim() && !inputDisabled) handleSubmitChat();
                  }
                }}
                placeholder={placeholder}
                data-testid="email-welcome-chat-input"
                disabled={inputDisabled}
                className="flex-1 outline-none"
                style={{
                  background: "transparent", border: "none", color: "white",
                  fontSize: 14, padding: "8px 0", fontFamily: "inherit",
                }}
              />
              <button
                type="submit"
                disabled={!chatInput.trim() || inputDisabled}
                data-testid="email-welcome-chat-send"
                aria-label="Küldés"
                style={{
                  width: 36, height: 36,
                  background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                  color: "white", borderRadius: 9, border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: chatInput.trim() && !inputDisabled ? "pointer" : "not-allowed",
                  opacity: chatInput.trim() && !inputDisabled ? 1 : 0.5,
                  boxShadow: chatInput.trim() && !inputDisabled
                    ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                    : "none",
                }}
              >
                {isLoading ? (
                  <CircleNotch size={15} weight="bold" className="animate-spin" />
                ) : (
                  <PaperPlaneTilt size={15} weight="fill" />
                )}
              </button>
            </div>
            <div className="flex justify-between" style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}>
              <span>Tipp: jelölj ki egy részt jobboldalt - a kérésed arra fog vonatkozni.</span>
              <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
            </div>
          </form>
        </div>

        {/* ─── RIGHT — krém dokumentum ─────────────────────────────── */}
        <div
          className="overflow-y-auto h-full"
          style={{ background: "#0a0a12", padding: "28px 36px 56px 36px" }}
        >
          <div
            ref={previewRef}
            data-preview-theme="cream"
            data-testid="email-welcome-cream-card"
            className="mr-auto cream-selectable"
            style={{
              background: "#f3eee4",
              color: "#0a0a12",
              borderRadius: 10,
              boxShadow: "0 16px 50px -22px rgba(0,0,0,0.50), 0 0 0 1px rgba(255,255,255,0.03)",
              padding: "32px 44px 56px",
              maxWidth: 860,
              marginLeft: 0,
              position: "relative",
              fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif",
            }}
          >
            {/* Cream control row — TASK 2: csak Másolás / Letöltés / Mentés */}
            <div className="flex justify-end items-center"
                 style={{ gap: 6, marginBottom: 28, position: "relative", zIndex: 1 }}>
              <CreamActionPill onClick={handleCopy}
                icon={copied ? CheckCircle : CopyIcon}
                label={copied ? "Másolva" : "Másolás"}
                testid="email-welcome-copy" />
              <CreamActionPill onClick={handleDownload}
                icon={DownloadSimple} label="Letöltés"
                testid="email-welcome-download" />
              <CreamActionPill onClick={handleSave}
                icon={saved ? CheckCircle : DownloadSimple}
                label={savingVault ? "Mentés…" : saved ? "Mentve" : "Mentés a Tudástárba"}
                testid="email-welcome-save" active={saved}
                disabled={savingVault || saved} />
            </div>

            {hasStructured ? (
              content.emails.map((em, i) => (
                <EmailBlock
                  key={em.email_szam || i}
                  email={em}
                  fixedTitle={WELCOME_FIXED_TITLES[i] || ""}
                  chosenIdx={chosenSubjects[em.email_szam] ?? 0}
                  onChoose={(idx) => setChosenSubjects((p) => ({ ...p, [em.email_szam]: idx }))}
                  onCopyOne={() => handleCopyOne(em)}
                  isFirst={i === 0}
                  isLast={i === content.emails.length - 1}
                />
              ))
            ) : (
              <pre style={{
                fontFamily: "inherit", fontSize: 15, lineHeight: 1.75,
                whiteSpace: "pre-wrap", color: "#2a2a35",
              }}>{rawText || ""}</pre>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── EmailBlock ─────────────────────────────────────────────────── */

function EmailBlock({ email, chosenIdx, onChoose, onCopyOne, isFirst, isLast, fixedTitle = "" }) {
  const { email_szam, nap, targysorok = [], body = "" } = email;
  const headerText = fixedTitle ? `Email #${email_szam} - ${fixedTitle}` : `Email #${email_szam}`;
  const timingValue = timingValueFromNap(nap);

  // Egyetlen forrás a 3 sub-eyebrow közös stílusához (TASK 4 spec): 13px,
  // uppercase, weight 600, letter-spacing 0.14em — IDENTIKUS minden
  // sub-eyebrowra ("A LEVÉL CÉLJA", "TÁRGYSOR-OPCIÓK", "SZÖVEGTÖRZS").
  const subEyebrowStyle = {
    fontSize: 13, color: ACCENT_BLUE, fontWeight: 600,
    letterSpacing: "0.14em", textTransform: "uppercase",
    marginBottom: 12,
  };

  return (
    <div
      data-testid={`email-welcome-block-${email_szam}`}
      style={{
        // TASK 1 spec: a "Email #N" cím lejjebb húzva. Az első e-mail
        // 32px top-margin-t kap → kompaktabb gap a kontroll pill-row és
        // az "Email #1" header között.
        marginTop: isFirst ? 32 : 0,
        marginBottom: isLast ? 0 : 52,
      }}
    >
      {/* (a) HEADER + TIMING TAG */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 14, marginBottom: 22, flexWrap: "wrap",
      }}>
        <div style={{
          fontSize: 21, fontWeight: 600, color: "#0a0a12",
          letterSpacing: "-0.01em", lineHeight: 1.25,
        }}>
          {headerText}
        </div>
        {/* Timing pill — solid #26A9C9, white text, clock icon prefix.
            Not a button; informational only. */}
        <span
          data-testid={`email-welcome-${email_szam}-timing`}
          aria-label={`Kiküldés: ${timingValue}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 12, fontWeight: 500,
            padding: "6px 12px", borderRadius: 999,
            background: ACCENT_BLUE,
            color: "#ffffff",
            whiteSpace: "nowrap",
            letterSpacing: "0.005em",
            border: "none",
            userSelect: "none",
            cursor: "default",
            pointerEvents: "none",
            boxShadow: "0 2px 6px -2px rgba(38,169,201,0.55)",
          }}
        >
          <Clock size={13} weight="bold" />
          {timingValue}
        </span>
      </div>

      {/* (b) PURPOSE LINE — ELTÁVOLÍTVA (user request 2026-06-02). */}

      {/* (c) TÁRGYSOR-OPCIÓK — faint blue pills (rgba(38,169,201,0.08)
           bg + 1px ACCENT_BLUE_BORDER), normal weight text. */}
      {targysorok.length > 0 && (
        <div style={{ marginBottom: 26 }}>
          <div style={subEyebrowStyle}>
            Tárgysor-opciók
          </div>
          <div className="flex flex-wrap gap-2">
            {targysorok.map((t, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onChoose(i)}
                data-testid={`email-welcome-${email_szam}-subject-${i}`}
                data-selected={i === chosenIdx ? "true" : "false"}
                style={{
                  fontSize: 14, fontWeight: 400,
                  padding: "8px 16px", borderRadius: 999,
                  background: SUBJECT_PILL_BG,
                  border: `1px solid ${SUBJECT_PILL_BORDER}`,
                  color: "#2a2a35",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "background 0.15s, border 0.15s",
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* (d) SZÖVEGTÖRZS — white card distinct from the cream surface.
           This IS the copyable email. Discreet copy icon bottom-right. */}
      <div style={subEyebrowStyle}>
        Szövegtörzs
      </div>
      <div
        data-testid={`email-welcome-${email_szam}-body-card`}
        style={{
          position: "relative",
          background: "#ffffff",
          border: "1px solid rgba(20,30,40,0.06)",
          borderRadius: 10,
          padding: "22px 26px 44px 26px",
          boxShadow: "0 1px 0 rgba(255,255,255,0.40) inset, 0 6px 18px -10px rgba(0,0,0,0.10)",
        }}
      >
        <BodyWithPlaceholders body={body} />
        <button
          type="button"
          onClick={onCopyOne}
          data-testid={`email-welcome-${email_szam}-copy-one`}
          aria-label="Levél másolása"
          title="Levél másolása"
          style={{
            position: "absolute", right: 10, bottom: 10,
            width: 30, height: 30, borderRadius: 8,
            background: "transparent",
            border: "1px solid rgba(20,30,40,0.10)",
            color: ACCENT_BLUE_DARK,
            cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            transition: "background 180ms ease, border 180ms ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = ACCENT_BLUE_TINT_BLOCK;
            e.currentTarget.style.border = `1px solid ${ACCENT_BLUE_BORDER_BLOCK}`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.border = "1px solid rgba(20,30,40,0.10)";
          }}
        >
          <CopyIcon size={14} weight="regular" />
        </button>
      </div>

      {/* (e) divider */}
      {!isLast && (
        <div style={{
          height: 0,
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          marginTop: 52,
        }} />
      )}
    </div>
  );
}

/* ─── BodyWithPlaceholders — [bracketed] inline editable chip ────────
   Speciális szabály: [Keresztnév] és [Név] (illetve [Keresztneved],
   [Neved] variánsok) plain szövegként jelennek meg, NEM chip-ként —
   ezek üres szögletes zárójeles megszólítás-/aláírás-mező-jelölők,
   amelyeket az e-mail kliens / a coach tölt ki, nincs szükségük inline
   chip-affordance-ra. Minden más [...] placeholder (linkek, dátum-
   helyek, számok) marad chip. */

const PLAIN_BRACKETS = new Set([
  "keresztnév", "keresztneved",
  "név", "neved",
  // Welcome writer és a Kézfelemelő writer is variánsokat használ a
  // signature-ben — mindet PLAIN-ként renderezzük az egységes look
  // érdekében (mind csak a coach saját nevét jelöli).
  "a te neved", "a neved", "coachod neve", "coach neve",
  "aláírás", "signature",
]);

function BodyWithPlaceholders({ body }) {
  if (!body) return null;
  const parts = [];
  const regex = /\[([^\]\n]{1,160})\]/g;
  let lastIdx = 0;
  let m;
  let keyCounter = 0;
  while ((m = regex.exec(body)) !== null) {
    if (m.index > lastIdx) {
      parts.push({ type: "text", text: body.slice(lastIdx, m.index), key: keyCounter++ });
    }
    const inner = m[1].trim();
    const isPlain = PLAIN_BRACKETS.has(inner.toLowerCase());
    parts.push({
      type: isPlain ? "plain-bracket" : "chip",
      text: inner,
      key: keyCounter++,
    });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < body.length) {
    parts.push({ type: "text", text: body.slice(lastIdx), key: keyCounter++ });
  }
  return (
    <div style={{
      fontSize: 15, color: "#2a2a35", lineHeight: 1.75,
      whiteSpace: "pre-wrap",
      fontFamily: "inherit",
    }}>
      {parts.map((p) => {
        if (p.type === "text") return <React.Fragment key={p.key}>{p.text}</React.Fragment>;
        if (p.type === "plain-bracket") return <React.Fragment key={p.key}>[{p.text}]</React.Fragment>;
        return <PlaceholderChip key={p.key} text={p.text} />;
      })}
    </div>
  );
}

function PlaceholderChip({ text }) {
  const [value, setValue] = useState(text);
  const ref = useRef(null);
  return (
    <span
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      onBlur={(e) => setValue(e.currentTarget.innerText.trim())}
      data-testid="email-placeholder-chip"
      data-original={text}
      style={{
        // Subtle, premium highlight - NOT a developer-input dashed chip.
        // Soft cream-tinted bg, hairline border, slight color shift only.
        display: "inline-block",
        padding: "0 4px",
        margin: "0 1px",
        borderRadius: 4,
        background: "rgba(38, 169, 201, 0.06)",
        boxShadow: "inset 0 -1px 0 rgba(38, 169, 201, 0.22)",
        color: "#1f3a45",
        fontWeight: 500,
        fontSize: "0.97em",
        lineHeight: "inherit",
        cursor: "text",
        outline: "none",
      }}
    >
      {value}
    </span>
  );
}

/* ─── CreamActionPill — cream felületi akció gomb (#26A9C9 accent) ─ */

function CreamActionPill({ onClick, icon: Icon, label, testid, active, disabled }) {
  const accentBg = active ? "rgba(29,158,117,0.10)" : ACCENT_BLUE_TINT_PILL;
  const accentBorder = active ? "rgba(29,158,117,0.30)" : "rgba(38, 169, 201,0.35)";
  const accentText = active ? "#1d9e75" : ACCENT_BLUE_DARK;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px",
        background: accentBg,
        border: `1px solid ${accentBorder}`,
        borderRadius: 999,
        color: accentText,
        fontSize: 12, fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap",
        fontFamily: "inherit",
        transition: "all 180ms ease",
      }}
    >
      <Icon size={13} weight="regular" /> {label}
    </button>
  );
}
